import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Card, CardContent, Typography, CircularProgress, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Box, FormControl, InputLabel, Select, MenuItem, Tabs, Tab, useMediaQuery,
  useTheme, AppBar, Toolbar, IconButton, Stack, TextField, TablePagination, Button,
  LinearProgress
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, startOfMonth, endOfMonth, subDays, subMonths, parseISO, isAfter, isBefore, isEqual , startOfToday} from 'date-fns';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  TrendingUp, AccountCircle, CreditCard, FilterList, PieChart as PieChartIcon,
  ShowChart, Assessment, CompareArrows, FileDownload
} from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as XLSX from 'xlsx';
import PageLoader from './pageLoader';

// --- Client-side response cache (survives re-renders, cleared on page refresh) ---
const _dashboardCache = new Map();

const COLORS = JSON.parse(process.env.REACT_APP_CHART_COLORS || '["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d", "#ffc658", "#8dd1e1"]');

const formatLocalDate = (date) => {
  if (!(date instanceof Date) || isNaN(date)) {
    return new Date().toISOString().slice(0, 10);
  }
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60 * 1000);
  return localDate.toISOString().slice(0, 10);
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const SubscriptionDashboard = (props) => {
  const {selectedApp, APP_LIST, setLoading, loading} = props;
  const [subs, setSubs] = useState([]);
  const [filteredSubs, setFilteredSubs] = useState([]);
  
  const getValidDate = (date) => {
    if (!date) return new Date();
    const parsedDate = new Date(date);
    return isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
  };

  const [startDate, setStartDate] = useState(() => {
    const saved = localStorage.getItem(process.env.REACT_APP_STORAGE_START_DATE);
    return getValidDate(saved) || subDays(new Date(), 30);
  });
  
  const [endDate, setEndDate] = useState(() => {
    const saved = localStorage.getItem(process.env.REACT_APP_STORAGE_END_DATE);
    return getValidDate(saved) || new Date();
  });
  
  const [dateRange, setDateRange] = useState(() => localStorage.getItem(process.env.REACT_APP_STORAGE_DATE_RANGE) || '30days');
  const [tabValue, setTabValue] = useState(() => parseInt(localStorage.getItem(process.env.REACT_APP_STORAGE_TAB_VALUE)) || 0);
  const [productFilter, setProductFilter] = useState(() => localStorage.getItem(process.env.REACT_APP_STORAGE_PRODUCT_FILTER) || 'all');
  
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  
  const [day1, setDay1] = useState(today);
  const [day2, setDay2] = useState(yesterday);
  
  const [day1Metrics, setDay1Metrics] = useState({});
  const [day2Metrics, setDay2Metrics] = useState({});
  const [todaySubs, setTodaySubs] = useState(0);
  const [newTodaySubs, setNewTodaySubs] = useState(0);
  const [renewalTodaySubs, setRenewalTodaySubs] = useState(0);
  const [todayRevenue, setTodayRevenue] = useState(0);
  const [todayCancellations, setTodayCancellations] = useState(0);
  const [todayExpired, setTodayExpired] = useState(0);
  const [activeSubs, setActiveSubs] = useState(0);
  const [todayInstalls, setTodayInstalls] = useState(0);
  const [todayUninstalls, setTodayUninstalls] = useState(0);
  const [todayRefunds, setTodayRefunds] = useState(0);
  const [dailyInstalls, setDailyInstalls] = useState(null);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [dailySubsData, setDailySubsData] = useState([]);
  const [dailyData, setDailyData] = useState([]);

  const fetchRequestId = useRef(0);

  const applyDashboardData = useCallback((data) => {
    const subscriptions = data.subscriptions || [];
    setSubs(subscriptions);
    filterSubscriptions(subscriptions, productFilter);

    setTodaySubs(data.todaySubscriptions || 0);
    setNewTodaySubs(data.newTodaySubscriptions || 0);
    setRenewalTodaySubs(data.renewalTodaySubscriptions || 0);
    setTodayRevenue(data.todayRevenue || 0);
    setActiveSubs(data.activeSubscriptions || 0);
    setTodayCancellations(data.todayCancellations || 0);
    setTodayExpired(data.todayExpired || 0);
    setTodayInstalls(data.todayInstalls || 0);
    setTodayUninstalls(data.todayUninstalls || 0);
    setTodayRefunds(data.todayRefunds || 0);

    const expiredLookup = {};
    if (data.daily_expired) {
      data.daily_expired.forEach(item => {
        expiredLookup[item.date] = item.expiredCount;
      });
    }

    const processedData = (data.daily_metrics || []).map(metric => ({
      date: metric.date,
      revenue: metric.revenue || 0,
      newCount: metric.newSubscriptions || 0,
      renewalCount: metric.renewalSubscriptions || 0,
      todayCancellations: metric.cancellations || 0,
      expiredCount: expiredLookup[metric.date] || metric.expired || 0,
      first_open: metric.installs || 0,
      app_remove: metric.uninstalls || 0
    }));

    setDailySubsData(processedData);
    setDailyData(processedData.map(d => ({ date: d.date, revenue: d.revenue || 0 })));
    setDailyInstalls(processedData.map(d => ({ date: d.date, first_open: d.first_open || 0, app_remove: d.app_remove || 0 })));
    setLastRefreshed(new Date());
  }, [productFilter]);

  const fetchDashboard = useCallback(async (appId, start, end) => {
    const validStart = getValidDate(start);
    const validEnd = getValidDate(end);
    if (!appId || !validStart || !validEnd) return;

    const startStr = formatLocalDate(validStart);
    const endStr = formatLocalDate(validEnd);
    const cacheKey = `${appId}|${startStr}|${endStr}`;
    const requestId = ++fetchRequestId.current;

    const cached = _dashboardCache.get(cacheKey);
    if (cached) {
      applyDashboardData(cached);
      setLoading(false);
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const params = new URLSearchParams({
        app_id: appId,
        start_date: startStr,
        end_date: endStr,
      });
      const res = await fetch(`${baseUrl}/dashboard-data?${params}`);
      const data = await res.json();

      if (requestId !== fetchRequestId.current) return;

      _dashboardCache.set(cacheKey, data);
      applyDashboardData(data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (!cached && process.env.REACT_APP_ENABLE_ERROR_ALERTS === 'true') {
        alert('Error fetching data from server');
      }
    } finally {
      if (requestId === fetchRequestId.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [applyDashboardData, setLoading]);

  useEffect(() => {
    if (day1 && day2) {
      setLoadingCharts(true);
      handleDayComparison();
    }
  }, [day1, day2]);
  
  useEffect(() => {
    const validStartDate = getValidDate(startDate);
    const validEndDate = getValidDate(endDate);
    if (validStartDate) localStorage.setItem(process.env.REACT_APP_STORAGE_START_DATE, validStartDate.toISOString());
    if (validEndDate) localStorage.setItem(process.env.REACT_APP_STORAGE_END_DATE, validEndDate.toISOString());
  }, [startDate, endDate]);
  
  useEffect(() => { localStorage.setItem(process.env.REACT_APP_STORAGE_DATE_RANGE, dateRange); }, [dateRange]);
  useEffect(() => { localStorage.setItem(process.env.REACT_APP_STORAGE_PRODUCT_FILTER, productFilter); }, [productFilter]);
  useEffect(() => { localStorage.setItem(process.env.REACT_APP_STORAGE_TAB_VALUE, tabValue); }, [tabValue]);
  
  useEffect(() => {
    if (!selectedApp || !startDate || !endDate) return;

    const debounceTimer = setTimeout(() => {
      fetchDashboard(selectedApp, startDate, endDate);
    }, 300);

    let intervalId;
    if (process.env.REACT_APP_ENABLE_AUTO_REFRESH === 'true') {
      intervalId = setInterval(() => {
        fetchDashboard(selectedApp, startDate, endDate);
      }, parseInt(process.env.REACT_APP_AUTO_REFRESH_INTERVAL) || 120000);
    }

    return () => {
      clearTimeout(debounceTimer);
      if (intervalId) clearInterval(intervalId);
    };
  }, [selectedApp, startDate, endDate, fetchDashboard]);

  useEffect(() => {
    filterSubscriptions(subs, productFilter);
  }, [productFilter]);

  const filterSubscriptions = (subscriptions, product) => {
    let filtered = subscriptions;
    
    if (product !== 'all') {
      filtered = filtered.filter(sub => sub.productSku?.includes(product));
    }
    
    setFilteredSubs(filtered);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    let newStartDate;
    const now = new Date();
  
    switch (range) {
      case 'last24':
        newStartDate = new Date(Date.now() - 24 * 60 * 60 * 1000 - 2 * 60 * 60 * 1000);
        break;
      case '7days':
        newStartDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000 - 2 * 60 * 60 * 1000);
        break;
      case '30days':
        newStartDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000 - 2 * 60 * 60 * 1000);
        break;
      case '90days':
        newStartDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000 - 2 * 60 * 60 * 1000);
        break;
      case 'thisMonth':
        newStartDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
        break;
      case 'lastMonth': {
        const firstOfLastMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
        const endOfLastMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0, 23, 59, 59));
        newStartDate = firstOfLastMonth;
        setEndDate(endOfLastMonth);
        break;
      }
      default:
        newStartDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    }
  
    setStartDate(newStartDate);
    if (range !== 'lastMonth') {
      setEndDate(new Date()); // current UTC time
    }
    console.log('end date', endDate);
  };  

  // const handleSort = (key) => {
  //   let direction = 'asc';
  //   if (sortConfig.key === key && sortConfig.direction === 'asc') {
  //     direction = 'desc';
  //   }
  //   setSortConfig({ key, direction });
  // };

  // const sortedSubs = React.useMemo(() => {
  //   let sortableItems = [...filteredSubs];
  //   if (sortConfig.key) {
  //     sortableItems.sort((a, b) => {
  //       if (a[sortConfig.key] < b[sortConfig.key]) {
  //         return sortConfig.direction === 'asc' ? -1 : 1;
  //       }
  //       if (a[sortConfig.key] > b[sortConfig.key]) {
  //         return sortConfig.direction === 'asc' ? 1 : -1;
  //       }
  //       return 0;
  //     });
  //   }
  //   return sortableItems;
  // }, [filteredSubs, sortConfig]);

  // Calculate revenue metrics
  const filteredRevenue = filteredSubs.reduce((sum, sub) => sum + parseFloat(sub.convertedPrice || 0), 0);

  const dailyDataMap = {};
  const dailySubsMap = {};

  filteredSubs.forEach(sub => {
    if (!sub.isSandbox) {
      const date = new Date(sub.purchaseDate).toISOString().slice(0, 10);
      if (!dailyDataMap[date]) {
        dailyDataMap[date] = { date, revenue: 0 };
      }
      dailyDataMap[date].revenue += sub.convertedPrice || 0;
    }
  });

  // Use API data for dailySubsMap instead of local calculation
  dailySubsData.forEach(day => {
    dailySubsMap[day.date] = {
      date: day.date,
      newCount: day.newCount,
      renewalCount: day.renewalCount,
      todayCancellations: day.todayCancellations,
      expiredCount: day.expiredCount || 0
    };
  });

  const prepareProductData = () => {
    const data = {};
    filteredSubs.forEach(sub => {
      // Only include active subscriptions
      if (sub.isSandbox || !sub.isSubscriptionActive) return;
      
      // Check if subscription has expired
      if (sub.expirationDate) {
        const expirationDate = new Date(sub.expirationDate);
        const currentDate = new Date();
        if (expirationDate < currentDate) return;
      }
      
      const sku = sub.productSku || 'Unknown';
      if (!data[sku]) {
        data[sku] = { name: sku, value: 0, count: 0 };
      }
      data[sku].value += parseFloat(sub.convertedPrice || 0);
      data[sku].count += 1;
    });
    const allProducts = Object.values(data).sort((a, b) => b.value - a.value);
    const totalRevenue = allProducts.reduce((sum, item) => sum + item.value, 0);
    const totalCount = allProducts.reduce((sum, item) => sum + item.count, 0);
    return allProducts.map(item => ({
      ...item,
      revenuePercent: totalRevenue > 0 ? (item.value / totalRevenue) * 100 : 0,
      countPercent: totalCount > 0 ? (item.count / totalCount) * 100 : 0
    }));
  };

  const productData = prepareProductData();

  // Get unique product types
  const productTypes = [...new Set(subs.map(s => s.productSku).filter(Boolean))];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const fetchDaysCompare = async (appId, date1, date2) => {
    const params = new URLSearchParams();
    if (appId) params.append('app_id', appId);

    params.append('date1', formatLocalDate(date1));
    params.append('date2', formatLocalDate(date2));

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/compare-revenue?${params.toString()}`);
      const data = await res.json();
      setDay1Metrics(data.date1_metrics);
      setDay2Metrics(data.date2_metrics);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoadingCharts(false);
    }
  };

  const handleDayComparison = () => {
    if (day1 && day2) {
      fetchDaysCompare(selectedApp, day1, day2);
    }
  };

  const [sortConfig, setSortConfig] = useState({ key: 'purchaseDate', direction: 'desc' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    purchaseDate: '',
    productSku: '',
    convertedPrice: '',
    type: '',
    status: '',
    country: '',
    platform: '',
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleSearchChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0); // reset to first page
  };

  const applyFilters = (data) => {
    return data.filter(sub => {
      return (
        sub.purchaseDate.toLowerCase().includes(filters.purchaseDate.toLowerCase()) &&
        sub.productSku.toLowerCase().includes(filters.productSku.toLowerCase()) &&
        sub.country.toLowerCase().includes(filters.country.toLowerCase()) &&
        String(sub.convertedPrice).includes(filters.convertedPrice) &&
        (filters.type === '' || (sub.id === sub.originalPurchase ? 'New' : 'Renewal') === filters.type) &&
        (filters.status === '' || (sub.isSubscriptionActive ? 'Active' : 'Inactive') === filters.status) &&
        (filters.platform === '' || (sub.platform === 'ios' ? 'ios' : 'android') === filters.platform)
      );
    });
  };

  const sortedSubs = [...subs].sort((a, b) => {
    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const filteredSubs2 = applyFilters(sortedSubs);
  const paginatedSubs = filteredSubs2.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Revenue Over Time total
  const totalRevenue = dailyData.reduce((sum, d) => sum + d.revenue, 0);
  // Subscription Growth totals
  const totalNewSubs = dailySubsData.reduce((sum, d) => sum + d.newCount, 0);
  const totalRenewals = dailySubsData.reduce((sum, d) => sum + d.renewalCount, 0);
  const totalCancellations = dailySubsData.reduce((sum, d) => sum + d.todayCancellations, 0);
  const totalExpired = dailySubsData.reduce((sum, d) => sum + (d.expiredCount || 0), 0);
  // Installs Growth totals
  const totalInstalls = dailyInstalls ? dailyInstalls.reduce((sum, d) => sum + (d.first_open || 0), 0) : 0;
  const totalUninstalls = dailyInstalls ? dailyInstalls.reduce((sum, d) => sum + (d.app_remove || 0), 0) : 0;

  const exportToExcel = (data, filename) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  const exportTransactions = () => {
    const exportData = filteredSubs2.map(sub => ({
      Date: format(new Date(sub.purchaseDate), 'yyyy-MM-dd HH:mm:ss'),
      Product: sub.productSku,
      Revenue: sub.convertedPrice?.toFixed(2),
      Country: sub.country,
      Status: sub.isSubscriptionActive ? 'Active' : 'Inactive',
      Platform: sub.platform,
      Type: sub.id === sub.originalPurchase ? 'New' : 'Renewal'
    }));
    exportToExcel(exportData, `transactions_${format(new Date(), 'yyyy-MM-dd')}`);
  };

  const exportDailyData = () => {
    const exportData = dailyData.map(day => ({
      Date: day.date,
      Revenue: day.revenue.toFixed(2)
    }));
    exportToExcel(exportData, `daily_revenue_${format(new Date(), 'yyyy-MM-dd')}`);
  };

  const exportSubscriptionGrowth = () => {
    const exportData = dailySubsData.map(day => {
      const row = {
        Date: day.date,
        'New Subscriptions': day.newCount,
        Renewals: day.renewalCount,
        Cancellations: day.todayCancellations,
        Expired: day.expiredCount || 0,
      };
      return row;
    });
    exportToExcel(exportData, `subscription_growth_${format(new Date(), 'yyyy-MM-dd')}`);
  };

  const exportInstallsData = () => {
    if (!dailyInstalls) return;
    const exportData = dailyInstalls.map(day => ({
      Date: day.date,
      Installs: day.first_open || 0,
      Uninstalls: day.app_remove || 0
    }));
    exportToExcel(exportData, `installs_data_${format(new Date(), 'yyyy-MM-dd')}`);
  };

  return (
    <>
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {/* Thin refresh bar when updating cached data */}
      {refreshing && (
        <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1300 }} />
      )}
      {/* Main Content */}
      {loading ? (
        <PageLoader/>
      ) : (
          <Box>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3, pt: 7, width: '100%' }} justifyContent={"center"}>
              
              <Grid container spacing={1} alignItems="center" justifyContent={"center"}>
                <Typography variant="h6">
                  <FilterList fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Filters
                </Typography>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Date Range</InputLabel>
                    <Select
                      value={dateRange}
                      label="Date Range"
                      onChange={(e) => handleDateRangeChange(e.target.value)}
                    >
                      <MenuItem value="last24">Last 24 Hours</MenuItem>
                      <MenuItem value="7days">Last 7 Days</MenuItem>
                      <MenuItem value="30days">Last 30 Days</MenuItem>
                      <MenuItem value="90days">Last 90 Days</MenuItem>
                      <MenuItem value="thisMonth">This Month</MenuItem>
                      <MenuItem value="lastMonth">Last Month</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(date) => setStartDate(date)}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(date) => setEndDate(date)}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Product</InputLabel>
                    <Select
                      value={productFilter}
                      label="Product"
                      onChange={(e) => setProductFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Products</MenuItem>
                      {productTypes.map((type, index) => (
                        <MenuItem key={index} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {/* Key Metrics Cards */}
            <Grid container spacing={3} justifyContent={"center"}>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%', backgroundColor: '#f9f9f9' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Today's Revenue</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CreditCard sx={{ color: 'green', mr: 1 }} />
                      <Typography variant="h4" color="green">${todayRevenue.toFixed(2)}</Typography>
                    </Box>
                    <Typography color="textSecondary" sx={{ mt: 1 }}>
                      {todaySubs} Subscriptions Today
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%', backgroundColor: '#f9f9f9' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Today's subscriptions</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountCircle sx={{ color: 'green', mr: 1 }} />
                      <Typography variant="h5" color="green">{newTodaySubs} New</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountCircle sx={{ color: 'primary.main', mr: 1 }} />
                      <Typography variant="h5" color="primary">{renewalTodaySubs} Renewals</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountCircle sx={{ color: '#d32f2f', mr: 1 }} />
                      <Typography variant="h5" color="error">{todayCancellations} Cancellations</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountCircle sx={{ color: '#ff9800', mr: 1 }} />
                      <Typography variant="h5" sx={{ color: '#ff9800' }}>{todayExpired} Expired</Typography>
                    </Box>
                    {todayRefunds > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccountCircle sx={{ color: '#f44336', mr: 1 }} />
                        <Typography variant="h6" sx={{ color: '#f44336' }}>{todayRefunds} Refunds</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%', backgroundColor: '#f9f9f9' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Today's installs</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountCircle sx={{ color: 'green', mr: 1 }} />
                      <Typography variant="h5" color="green">{todayInstalls} Installs</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountCircle sx={{ color: '#d32f2f', mr: 1 }} />
                      <Typography variant="h5" color="error">{todayUninstalls} Uninstalls</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%', backgroundColor: '#f9f9f9' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Active Subscriptions</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountCircle sx={{ color: 'primary.main', mr: 1 }} />
                      <Typography variant="h4" color="primary">{activeSubs}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%', backgroundColor: '#f9f9f9' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Filtered Period Revenue
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TrendingUp sx={{ color: 'primary.main', mr: 1 }} />
                      <Typography variant="h4" color="primary">${filteredRevenue.toFixed(2)}</Typography>
                    </Box>
                    <Typography color="textSecondary" sx={{ mt: 1 }}>
                      {filteredSubs.length} Transactions
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              {/*<Grid item xs={12} md={3}>
                <Card sx={{ height: '100%', backgroundColor: '#f9f9f9' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Monthly Recurring Revenue</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CalendarToday sx={{ color: 'primary.main', mr: 1 }} />
                      <Typography variant="h4" color="primary">${mrr.toFixed(2)}</Typography>
                    </Box>
                    <Typography color="textSecondary" sx={{ mt: 1 }}>
                      All-Time: ${totalRevenue.toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>*/}
            </Grid>

            {/* Tabs for different chart views */}
            <Paper sx={{ mt: 3 }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                variant="scrollable"
                scrollButtons="auto"
                aria-label="dashboard tabs"
              >
                <Tab icon={<ShowChart />} label="Revenue Trends" />
                <Tab icon={<PieChartIcon />} label="Revenue by Product" />
                <Tab icon={<Assessment />} label="Transaction Details" />
                <Tab icon={<CompareArrows />} label="Compare Days" />
              </Tabs>
            </Paper>

            {/* Tab Content */}
            <Box sx={{ mt: 3 }} justifyContent={"center"}>
              {/* Revenue Trends Tab */}
              {tabValue === 0 && (
                <Grid container spacing={3} justifyContent={"center"}>
                  <Grid item xs={12} md={12} sx={{width: '100%'}}>
                    <Card sx={{width: '100%'}}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">Revenue Over Time</Typography>
                          <Button
                            variant="contained"
                            startIcon={<FileDownload />}
                            onClick={exportDailyData}
                            size="small"
                          >
                            Export Data
                          </Button>
                        </Box>
                        <Typography variant="subtitle1" color="primary" sx={{ mb: 1 }}>
                          Total: ${totalRevenue.toFixed(2)}
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={dailyData} margin={{ top: 10, right: 30, left: 20, bottom: 30 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" angle={-45} textAnchor="end" height={45} />
                            <YAxis />
                            <Tooltip formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']} />
                            <Line 
                              type="monotone" 
                              dataKey="revenue" 
                              stroke="#3f51b5" 
                              name="Revenue" 
                              legendType='none'
                              strokeWidth={2} 
                              dot={{ r: 3 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={12} sx={{width: '100%'}}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">Subscription Growth</Typography>
                          <Button
                            variant="contained"
                            startIcon={<FileDownload />}
                            onClick={exportSubscriptionGrowth}
                            size="small"
                          >
                            Export Data
                          </Button>
                        </Box>
                        <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                          <Typography color="success.main">New: {totalNewSubs}</Typography>
                          <Typography color="primary.main">Renewals: {totalRenewals}</Typography>
                          <Typography color="error.main">Cancellations: {totalCancellations}</Typography>
                          <Typography sx={{ color: '#ff9800' }}>Expired: {totalExpired}</Typography>
                        </Stack>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={dailySubsData} margin={{ top: 10, right: 30, left: 20, bottom: 30 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" angle={-45} textAnchor="end" height={75} />
                            <YAxis />
                            <Tooltip 
                              formatter={(value, name) => {
                                const labelMap = {
                                  newCount: 'New Subscriptions',
                                  renewalCount: 'Renewals',
                                  todayCancellations: 'Cancellations',
                                  expiredCount: 'Expired',
                                };
                                return [value, labelMap[name] || name];
                              }} 
                            />
                            <Bar dataKey="newCount" name="New Subscriptions" stackId="a" fill="#82ca9d" />
                            <Bar dataKey="renewalCount" name="Renewals" stackId="a" fill="#3f51b5" />
                            <Bar dataKey="todayCancellations" name="Cancellations" stackId="b" fill="#d32f2f" />
                            <Bar dataKey="expiredCount" name="Expired" stackId="b" fill="#ff9800" />
                            </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={12} sx={{width: '100%'}}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">Installs Growth</Typography>
                          <Button
                            variant="contained"
                            startIcon={<FileDownload />}
                            onClick={exportInstallsData}
                            size="small"
                          >
                            Export Data
                          </Button>
                        </Box>
                        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                          <Typography color="success.main">Installs: {totalInstalls}</Typography>
                          <Typography color="error.main">Uninstalls: {totalUninstalls}</Typography>
                        </Stack>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={dailyInstalls} margin={{ top: 10, right: 30, left: 20, bottom: 30 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" angle={-45} textAnchor="end" height={75} />
                            <YAxis />
                            <Tooltip 
                              formatter={(value, name) => {
                                const labelMap = {
                                  first_open: 'Installs',
                                  app_remove: 'Uninstalls'
                                };
                                return [value, labelMap[name] || name];
                              }} 
                            />
                            <Bar dataKey="first_open" name="Installs" stackId="a" fill="#82ca9d" />
                            <Bar dataKey="app_remove" name="Uninstalls" stackId="a" fill="#d32f2f" />
                            </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Revenue by Product Tab */}
              {tabValue === 1 && (
                <Grid container spacing={3} justifyContent={"center"}>
                  <Grid item xs={12} md={6} sx={{width: '100%'}}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">Revenue by Product</Typography>
                        <ResponsiveContainer width="100%" height={350}>
                          <PieChart>
                            <Pie
                              data={productData.slice(0, 5)}
                              cx="50%"
                              cy="50%"
                              labelLine={true}
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="value"
                              nameKey="name"
                              label={(entry) => {
                                const dataEntry = productData.slice(0, 5).find(item => item.name === entry.name);
                                return dataEntry ? `${dataEntry.revenuePercent.toFixed(1)}%` : '';
                              }}
                            >
                              {productData.slice(0, 5).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value, name, props) => [props.payload.count, 'Count']} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{width: '100%'}}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Subscription Count by Product</Typography>
                        <ResponsiveContainer width="100%" height={350}>
                          <PieChart>
                            <Pie
                              data={productData.slice(0, 5)}
                              cx="50%"
                              cy="50%"
                              labelLine={true}
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="count"
                              nameKey="name"
                              label={(entry) => {
                                const dataEntry = productData.slice(0, 5).find(item => item.name === entry.name);
                                return dataEntry ? `${dataEntry.countPercent.toFixed(1)}%` : '';
                              }}
                            >
                              {productData.slice(0, 5).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => [value, 'Count']} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{width: '100%'}}>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell align="right">Subscriptions</TableCell>
                            <TableCell align="right">Revenue</TableCell>
                            <TableCell align="right">Avg. Price</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {productData.map((row, index) => (
                            <TableRow key={index}>
                              <TableCell component="th" scope="row">{row.name}</TableCell>
                              <TableCell align="right">{row.count}</TableCell>
                              <TableCell align="right">${row.value.toFixed(2)}</TableCell>
                              <TableCell align="right">${(row.value / row.count).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              )}

              {/* Transaction Details Tab */}
              {tabValue === 2 && (
                <Grid container spacing={4} justifyContent="center">
               {/* Comparison and Chart */}
               <Grid item xs={12} md={8} width="100%">
                <Paper>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<FileDownload />}
                      onClick={exportTransactions}
                      size="small"
                    >
                      Export Transactions
                    </Button>
                  </Box>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TextField
                              variant="standard"
                              placeholder="Search Date"
                              value={filters.purchaseDate}
                              onChange={(e) => handleSearchChange('purchaseDate', e.target.value)}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="standard"
                              placeholder="Search Product"
                              value={filters.productSku}
                              onChange={(e) => handleSearchChange('productSku', e.target.value)}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="standard"
                              placeholder="Search Revenue"
                              value={filters.convertedPrice}
                              onChange={(e) => handleSearchChange('convertedPrice', e.target.value)}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="standard"
                              placeholder="Search Country"
                              value={filters.country}
                              onChange={(e) => handleSearchChange('country', e.target.value)}
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl variant="standard" fullWidth>
                              <InputLabel>Status</InputLabel>
                              <Select
                                value={filters.status}
                                onChange={(e) => handleSearchChange('status', e.target.value)}
                                label="Status"
                              >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="Active">Active</MenuItem>
                                <MenuItem value="Inactive">Inactive</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <FormControl variant="standard" fullWidth>
                              <InputLabel>Platform</InputLabel>
                              <Select
                                value={filters.platform}
                                onChange={(e) => handleSearchChange('platform', e.target.value)}
                                label="Platform"
                              >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="ios">iOS</MenuItem>
                                <MenuItem value="android">Android</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <FormControl variant="standard" fullWidth>
                              <InputLabel>Type</InputLabel>
                              <Select
                                value={filters.type}
                                onChange={(e) => handleSearchChange('type', e.target.value)}
                                label="Type"
                              >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="New">New</MenuItem>
                                <MenuItem value="Renewal">Renewal</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell onClick={() => handleSort('purchaseDate')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                            Date {sortConfig.key === 'purchaseDate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </TableCell>
                          <TableCell onClick={() => handleSort('productSku')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                            Product {sortConfig.key === 'productSku' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </TableCell>
                          <TableCell onClick={() => handleSort('convertedPrice')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                            Revenue {sortConfig.key === 'convertedPrice' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Country</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Platform</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedSubs.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 6, color: "text.secondary" }}>
                              No transactions found.
                            </TableCell>
                          </TableRow>
                        ) : (
                        paginatedSubs.map((sub) => (
                          <TableRow key={sub.id}>
                            <TableCell>{format(new Date(sub.purchaseDate), 'yyyy-MM-dd HH:mm:ss')}</TableCell>
                            <TableCell>{sub.productSku}</TableCell>
                            <TableCell>{'$'}{sub.convertedPrice?.toFixed(2)}</TableCell>
                            <TableCell>{sub.country}</TableCell><TableCell>
                              {sub.isSubscriptionActive
                                ? <span style={{ color: 'green' }}>Active</span>
                                : <span style={{ color: 'red' }}>Inactive</span>
                              }
                            </TableCell>
                            <TableCell>{sub.platform}</TableCell>
                            <TableCell>{sub.id === sub.originalPurchase ? 'New' : 'Renewal'}</TableCell>
                          </TableRow>
                        )))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    component="div"
                    count={filteredSubs2.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    sx={{ width: "100%" }}
                  />
                </Paper>
                </Grid>
                </Grid>
              )}
              {/* Compare 2 days Tab */}
              {tabValue === 3 && (
               <Grid container spacing={4} justifyContent="center">
               {/* Comparison and Chart */}
               <Grid item xs={12} md={8} width="100%">
                 <Card sx={{ p: 2 }}>
                   <CardContent>
                     <LocalizationProvider dateAdapter={AdapterDateFns}>
                       <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} alignItems="center">
                         <DatePicker label="Select Day 1" value={day1} onChange={setDay1} />
                         <DatePicker label="Select Day 2" value={day2} onChange={setDay2} />
                       </Stack>
                     </LocalizationProvider>
                     {loadingCharts ? (
                       <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                         <CircularProgress />
                       </Box>
                     ) : (
                       <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} alignItems="center">
                         <ResponsiveContainer width="100%" height={300}>
                           <BarChart data={[
                             {
                               name: 'Subscriptions',
                               [`New ${formatLocalDate(day1)}`]: day1Metrics.new_subscriptions ?? day1Metrics.subscriptions,
                               [`Renewal ${formatLocalDate(day1)}`]: day1Metrics.renewal_subscriptions ?? 0,
                               [`Cancellations ${formatLocalDate(day1)}`]: day1Metrics.cancellations ?? 0,
                               [`New ${formatLocalDate(day2)}`]: day2Metrics.new_subscriptions ?? day2Metrics.subscriptions,
                               [`Renewal ${formatLocalDate(day2)}`]: day2Metrics.renewal_subscriptions ?? 0,
                               [`Cancellations ${formatLocalDate(day2)}`]: day2Metrics.cancellations ?? 0,
                             }
                           ]}>
                             <XAxis dataKey="name" />
                             <YAxis />
                             <Tooltip />
                             <Bar dataKey={`New ${formatLocalDate(day1)}`} fill="#82ca9d" name={`New ${formatLocalDate(day1)}`}/>
                             <Bar dataKey={`Renewal ${formatLocalDate(day1)}`} fill="#3f51b0" name={`Renewal ${formatLocalDate(day1)}`}/>
                             <Bar dataKey={`Cancellations ${formatLocalDate(day1)}`} fill="#d32f2f" name={`Cancellations ${formatLocalDate(day1)}`}/>
                             <Bar dataKey={`New ${formatLocalDate(day2)}`} fill="#82ca9d" name={`New ${formatLocalDate(day2)}`}/>
                             <Bar dataKey={`Renewal ${formatLocalDate(day2)}`} fill="#3f51b5" name={`Renewal ${formatLocalDate(day2)}`}/>
                             <Bar dataKey={`Cancellations ${formatLocalDate(day2)}`} fill="#d32f2f" name={`Cancellations ${formatLocalDate(day2)}`}/>
                           </BarChart>
                         </ResponsiveContainer>
                         <ResponsiveContainer width="100%" height={300}>
                           <BarChart data={[
                             {
                               name: 'Revenue',
                               [formatLocalDate(day1)]: day1Metrics.revenue ? day1Metrics.revenue.toFixed(2) : 0,
                               [formatLocalDate(day2)]: day2Metrics.revenue ? day2Metrics.revenue.toFixed(2) : 0,
                             }
                           ]}>
                             <XAxis dataKey="name" />
                             <YAxis />
                             <Tooltip />
                             <Bar dataKey={formatLocalDate(day1)} fill="#8884d8" />
                             <Bar dataKey={formatLocalDate(day2)} fill="#82ca9d" />
                           </BarChart>
                         </ResponsiveContainer>
                         {/* Installs & Uninstalls Comparison (merged) */}
                         <ResponsiveContainer width="100%" height={300}>
                           <BarChart data={[
                             {
                               name: 'Installs/Uninstalls',
                               [`Installs ${formatLocalDate(day1)}`]: day1Metrics.installs || 0,
                               [`Uninstalls ${formatLocalDate(day1)}`]: day1Metrics.uninstalls || 0,
                               [`Installs ${formatLocalDate(day2)}`]: day2Metrics.installs || 0,
                               [`Uninstalls ${formatLocalDate(day2)}`]: day2Metrics.uninstalls || 0,
                             }
                           ]}>
                             <XAxis dataKey="name" />
                             <YAxis />
                             <Tooltip />
                             <Bar dataKey={`Installs ${formatLocalDate(day1)}`} fill="#82ca9d" name={`Installs ${formatLocalDate(day1)}`}/>
                             <Bar dataKey={`Uninstalls ${formatLocalDate(day1)}`} fill="#d32f2f" name={`Uninstalls ${formatLocalDate(day1)}`}/>
                             <Bar dataKey={`Installs ${formatLocalDate(day2)}`} fill="#82ca9d" name={`Installs ${formatLocalDate(day2)}`}/>
                             <Bar dataKey={`Uninstalls ${formatLocalDate(day2)}`} fill="#d32f2f" name={`Uninstalls ${formatLocalDate(day2)}`}/>
                           </BarChart>
                         </ResponsiveContainer>
                       </Stack>
                     )}
                   </CardContent>
                 </Card>
               </Grid>
               {/* Table */}
               <Grid item xs={12} width="100%">
                 <Card>
                   <CardContent>
                     <Typography variant="h6" gutterBottom>
                       Subscription Details
                     </Typography>
                     <TableContainer component={Paper}>
                       <Table>
                         <TableHead>
                           <TableRow>
                             <TableCell>Date</TableCell>
                             <TableCell align="right">Subscriptions</TableCell>
                             <TableCell align="right">New Subs</TableCell>
                             <TableCell align="right">Renewals</TableCell>
                             <TableCell align="right">Revenue</TableCell>
                             <TableCell align="right">Installs</TableCell>
                             <TableCell align="right">Uninstalls</TableCell>
                             <TableCell align="right">Cancellations</TableCell>
                             <TableCell align="right">Expired</TableCell>
                             <TableCell align="right">Retry</TableCell>
                             <TableCell align="right">Refunds</TableCell>
                           </TableRow>
                         </TableHead>
                         <TableBody>
                           {[{ day: day1, metrics: day1Metrics }, { day: day2, metrics: day2Metrics }].map((entry, index) => (
                             <TableRow key={index}>
                               <TableCell>{formatLocalDate(entry.day)}</TableCell>
                               <TableCell align="right">{entry.metrics.subscriptions}</TableCell>
                               <TableCell align="right">{entry.metrics.new_subscriptions ?? entry.metrics.subscriptions}</TableCell>
                               <TableCell align="right">{entry.metrics.renewal_subscriptions ?? 0}</TableCell>
                               <TableCell align="right">${entry.metrics.revenue?.toFixed(2)}</TableCell>
                               <TableCell align="right">{entry.metrics.installs || 0}</TableCell>
                               <TableCell align="right">{entry.metrics.uninstalls || 0}</TableCell>
                               <TableCell align="right">{entry.metrics.cancellations ?? 0}</TableCell>
                               <TableCell align="right">{entry.metrics.expired ?? 0}</TableCell>
                               <TableCell align="right">{entry.metrics.renewal_retry ?? 0}</TableCell>
                               <TableCell align="right">{entry.metrics.refunds ?? 0}</TableCell>
                             </TableRow>
                           ))}
                         </TableBody>
                       </Table>
                     </TableContainer>
                   </CardContent>
                 </Card>
               </Grid>
             </Grid>
            )}
          </Box>
        </Box>) }
      </Box>
  </>
  );
};

export default SubscriptionDashboard;